/* iter-p3-s — Editor Provenance Widget.
 *
 * Compact card that surfaces the AI-vs-human token share for the
 * current project plus a one-click "Download provenance CSV" button.
 * Meant to sit in the editor sidebar; also usable standalone from
 * a settings panel.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProvenanceWidget({ projectId, token }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    if (!projectId) return;
    const cfg = token
      ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      : { withCredentials: true };
    axios.get(`${API}/api/projects/${projectId}/editing-record?page=1&page_size=1`, cfg)
      .then((r) => setSummary(r.data?.summary || null))
      .catch(() => setSummary(null));
  }, [projectId, token]);

  const downloadCsv = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const url = `${API}/api/projects/${projectId}/editing-record/export`;
      const r = await axios.get(url, {
        responseType: 'blob',
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `editing-record-${projectId.slice(0, 8)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_) { /* silent */ }
    finally { setBusy(false); }
  };

  const share = summary?.ai_share_pct;
  const shareColor = share == null ? 'text-muted-foreground'
    : share >= 66 ? 'text-amber-600'
    : share >= 34 ? 'text-emerald-600'
    : 'text-primary';

  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid="provenance-widget">
      <div className="mb-2 flex items-center gap-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Copyright provenance
      </div>
      {summary === null ? (
        <div className="py-2 text-[11.5px] text-muted-foreground" data-testid="provenance-empty">
          No AI actions recorded yet on this project.
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <div className={`text-2xl font-semibold tabular-nums ${shareColor}`} data-testid="provenance-ai-share">
              {share == null ? '—' : `${share}%`}
            </div>
            <div className="text-[11px] text-muted-foreground">AI-assisted share</div>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            <span data-testid="provenance-ai-count">{summary?.ai_actions ?? 0}</span> AI actions ·{' '}
            <span data-testid="provenance-human-count">{summary?.human_actions ?? 0}</span> human actions
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" title="AI share">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${Math.min(100, Math.max(0, share || 0))}%` }}
            />
          </div>
        </>
      )}
      <button
        type="button"
        onClick={downloadCsv}
        disabled={busy || !projectId}
        data-testid="provenance-download-csv"
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-sm border border-primary/40 bg-primary/10 px-2 py-1 text-[11.5px] font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Download provenance CSV
      </button>
      <p className="mt-2 text-[10px] leading-tight text-muted-foreground">
        Registration-ready. Includes model, prompt summary, token count, and human-edit delta for every action, aligned with U.S. Copyright Office / UK IPO / EU AI Act standards.
      </p>
    </div>
  );
}
